import React from "react";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import GroupTwoToneIcon from "@mui/icons-material/GroupTwoTone";
import VpnKeyTwoToneIcon from "@mui/icons-material/VpnKeyTwoTone";
import HistoryTwoToneIcon from "@mui/icons-material/HistoryTwoTone";
import type { SvgIconComponent } from "@mui/icons-material";
import "./RoleCard.css";

export type RoleCardProps = {
  icon: SvgIconComponent;
  name: string;
  memberCount: number;
  permissionCount: number;
  updatedAt: string;
  updatedByName?: string;
  onClick: () => void;
  onMenuClick: (anchor: HTMLElement) => void;
};

function formatRelative(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  const ms = d.getTime();
  if (Number.isNaN(ms)) return "—";
  const days = Math.floor((Date.now() - ms) / 86_400_000);
  if (days < 1) return "hoje";
  if (days === 1) return "ontem";
  if (days < 7) return `há ${days} dias`;
  if (days < 30) return `há ${Math.floor(days / 7)} sem`;
  if (days < 365) return `há ${Math.floor(days / 30)} meses`;
  return `há ${Math.floor(days / 365)} anos`;
}

/**
 * A role tile in the IAM list.
 *
 * `icon` is a `SvgIconComponent` — a type declared inside @mui/icons-material,
 * i.e. outside src/. The generator will not invent a value for a shape it does
 * not own (walking it structurally produces fiction), so without the line below
 * this component was skipped entirely: "required prop the type checker cannot
 * fill: icon". The fixture names a real icon, which is also what the IAM page
 * passes.
 *
 * @story fixture icon @mui/icons-material/GroupTwoTone#default
 */
export const RoleCard: React.FC<RoleCardProps> = (props) => {
  const Icon = props.icon;
  const handleMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    props.onMenuClick(e.currentTarget);
  };

  return (
    <button className="roleCard" onClick={props.onClick} type="button">
      <div className="roleCardHeader">
        <Icon className="roleCardIcon" />
        <span className="roleCardName">{props.name}</span>
        <button
          className="roleCardMenuBtn"
          onClick={handleMenu}
          aria-label="Mais ações"
          type="button"
        >
          <MoreVertIcon />
        </button>
      </div>
      <div className="roleCardMeta">
        <span className="roleCardMetaItem">
          <GroupTwoToneIcon fontSize="small" />
          {props.memberCount} colaboradores
        </span>
        <span className="roleCardMetaItem">
          <VpnKeyTwoToneIcon fontSize="small" />
          {props.permissionCount} permissões
        </span>
      </div>
      <div className="roleCardEdit">
        <HistoryTwoToneIcon fontSize="inherit" />
        <span>
          {formatRelative(props.updatedAt)}
          {props.updatedByName ? ` · ${props.updatedByName}` : ""}
        </span>
      </div>
    </button>
  );
};
